import styled from "styled-components";

import Typography from "../../../../typography/Typography";

import {
	StyledStepperStepProps,
	StyledStepperStepBulletProps,
	StyledStepperStepLabelProps,
} from "../StepperStep.types";

const TRANSITION_DURATION = "300ms";
const TRANSITION_TIMING_FUNCTION = "ease-in-out";
const STEP_BULLET_DIMENSION = "32px";

const StyledStepperStep = styled.div<StyledStepperStepProps>`
	display: flex;
	flex-direction: column;
	align-items: center;
	flex: 1;
	position: relative;

	&:not(:last-child) {
		${({ theme, status }) => {
			const STEP_BAR_SPACE = theme.space.xs;
			const STEP_BAR_HEIGHT = "6px";
			const STEP_BAR_WIDTH = `calc(100% - ${STEP_BULLET_DIMENSION} - 2 * ${STEP_BAR_SPACE})`;

			return {
				"::before, ::after": {
					content: "''",
					position: "absolute",
					left: `calc(50% + ${STEP_BULLET_DIMENSION} / 2 + ${STEP_BAR_SPACE})`,
					top: `calc(${STEP_BULLET_DIMENSION} / 2 - ${STEP_BAR_HEIGHT} / 2)`,
					height: STEP_BAR_HEIGHT,
					backgroundColor: theme.palette.hexForRole("cta_default"),
					borderRadius: theme.radii.sm,
				},
				"::before": {
					opacity: "0.3",
					width: STEP_BAR_WIDTH,
				},
				"::after": {
					width: status === "completed" ? STEP_BAR_WIDTH : 0,
					transition:
						status === "completed"
							? `width ${TRANSITION_DURATION} ${TRANSITION_TIMING_FUNCTION}`
							: "none",
				},
			};
		}}
	}
`;

export const StyledStepBullet = styled.span<StyledStepperStepBulletProps>`
	display: flex;
	position: relative;
	align-items: center;
	justify-content: center;
	border-radius: 50%;
	cursor: pointer;
	opacity: 1;
	transition: opacity ${TRANSITION_DURATION} ${TRANSITION_TIMING_FUNCTION};
	box-sizing: border-box;

	::after {
		content: "";
		display: block;
		position: absolute;
		border-radius: 50%;
		width: 100%;
		height: 100%;
		opacity: 0;
		transition: opacity ${TRANSITION_DURATION} ${TRANSITION_TIMING_FUNCTION};
	}

	${({ theme, status }) => {
		const COLOR_BG_WHITE = theme.palette.hexForRole("bg_white");
		const COLOR_CTA_DEFAULT = theme.palette.hexForRole("cta_default");
		const COLOR_PRIMARY_ACCENT_500 =
			theme.palette.hexForMandatory("primary_accent_500");

		return {
			width: STEP_BULLET_DIMENSION,
			height: STEP_BULLET_DIMENSION,
			marginBottom: theme.space.xs,
			...(status === "completed" && {
				background: `linear-gradient(180deg, ${COLOR_PRIMARY_ACCENT_500} 0%, ${COLOR_CTA_DEFAULT} 100%);`,
			}),
			...((status === "active" || status === "default") && {
				backgroundColor: COLOR_BG_WHITE,
				border: `2px solid ${COLOR_CTA_DEFAULT}`,
			}),
			...(status === "active" && {
				"::after": {
					opacity: 0.3,
					outline: `4px solid ${COLOR_CTA_DEFAULT}`,
				},
			}),
			...(status === "default" && {
				opacity: 0.3,
			}),
		};
	}}
`;

export const StyledStepLabel = styled(Typography)<StyledStepperStepLabelProps>`
	opacity: ${({ status }) => (status === "default" ? 0.3 : 1)};
	transition: opacity ${TRANSITION_DURATION} ${TRANSITION_TIMING_FUNCTION};
`;

export default StyledStepperStep;
